import { useState } from "react";
import "./SalaryCalculator.css";

const inr = (n) =>
  "₹" + Math.round(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const FORM_TABS = ["Structure", "Allowances", "Deductions"];
const FORM_ICONS = ["💼", "💰", "📉"];
const RES_TABS = ["Earnings", "Deductions", "Distribution"];

function Badge({ type }) {
  const map = {
    Taxable: "taxable",
    "Partly Exempt": "partly-exempt",
    Exempt: "exempt",
    Deduction: "deduction",
    PF: "pf",
    Gratuity: "gratuity",
  };
  return <span className={`sc-badge sc-badge--${map[type] || "taxable"}`}>{type}</span>;
}

function TableRow({ label, annual, monthly, badge, isTotal }) {
  return (
    <div className={`sc-trow${isTotal ? " sc-trow--total" : ""}`}>
      <span className="sc-trow-label">{label}</span>
      <span className="sc-trow-num">{inr(annual)}</span>
      <span className="sc-trow-num">{inr(monthly)}</span>
      {badge ? <Badge type={badge} /> : <span />}
    </div>
  );
}

function TableHead() {
  return (
    <div className="sc-thead">
      <span>Component</span>
      <span>Annual</span>
      <span>Monthly</span>
      <span>Type</span>
    </div>
  );
}

function DistBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div className="sc-dist-row">
      <span className="sc-dist-dot" style={{ background: color }} />
      <span className="sc-dist-label">{label}</span>
      <div className="sc-dist-track">
        <div className="sc-dist-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="sc-dist-pct">{pct.toFixed(1)}%</span>
      <span className="sc-dist-val">{inr(value)}</span>
    </div>
  );
}

function Toggle({ options, value, onChange }) {
  return (
    <div className="sc-toggle-group">
      {options.map(([v, l]) => (
        <button
          key={String(v)}
          className={`sc-tog${value === v ? " active" : ""}`}
          onClick={() => onChange(v)}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="sc-field">
      <label className="sc-field-label">{label}</label>
      {children}
    </div>
  );
}

function RupeeInput({ value, onChange, placeholder }) {
  return (
    <div className="sc-inp-wrap">
      <span className="sc-inp-sym">₹</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function PctBadge({ value }) {
  return <span className="sc-pct-badge">{value}%</span>;
}

function SliderField({ label, value, min, max, onChange, leftLabel, rightLabel }) {
  return (
    <Field label={<>{label} <PctBadge value={value} /></>}>
      <input
        type="range"
        min={min}
        max={max}
        step={1}
        value={value}
        onChange={(e) => onChange(+e.target.value)}
      />
      <div className="sc-slider-labels">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
    </Field>
  );
}

function StatMini({ label, value, colorClass }) {
  return (
    <div className="sc-stat-mini">
      <div className="sc-stat-lbl">{label}</div>
      <div className={`sc-stat-val${colorClass ? " " + colorClass : ""}`}>{value}</div>
    </div>
  );
}

export default function SalaryCalculator() {
  const [formTab, setFormTab] = useState(0);
  const [resTab, setResTab] = useState(0);
  const [form, setForm] = useState({
    ctc: "",
    basicPct: 40,
    hraPct: 50,
    lta: "",
    mealMonthly: "",
    bonus: "",
    profTaxMonthly: 200,
    pfInCTC: true,
    gratuityInCTC: true,
    cityType: "metro",
  });
  const [result, setResult] = useState(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (k) => parseFloat(form[k]) || 0;

  function calculate() {
    const ctc = num("ctc");
    if (!ctc) return;
    const basic = ctc * (form.basicPct / 100);
    const basicMonthly = basic / 12;
    const hraAnnual = basic * (form.hraPct / 100);
    const ltaAnnual = num("lta");
    const mealAnnual = num("mealMonthly") * 12;
    const bonusAnnual = num("bonus");
    const empPF = form.pfInCTC ? Math.min(basic * 0.12, 21600) : 0;
    const gratuity = form.gratuityInCTC ? basic * (4.81 / 100) : 0;
    const grossAnnual = ctc - empPF - gratuity;
    const grossMonthly = grossAnnual / 12;
    const specialAllow = Math.max(
      0,
      grossAnnual - basic - hraAnnual - ltaAnnual - mealAnnual - bonusAnnual
    );
    const eePF = Math.min(basicMonthly * 0.12, 1800) * 12;
    const profTax = num("profTaxMonthly") * 12;
    const esic = grossMonthly <= 21000 ? grossMonthly * 0.0075 * 12 : 0;
    const totalEeDed = eePF + profTax + esic;
    const netAnnual = grossAnnual - totalEeDed;
    const netMonthly = netAnnual / 12;
    const hraExemptFactor = form.cityType === "metro" ? 0.5 : 0.4;
    const hraExempt = Math.min(hraAnnual, basic * hraExemptFactor);
    setResult({
      ctc, grossAnnual, grossMonthly, netAnnual, netMonthly,
      basic, hraAnnual, hraExempt, ltaAnnual, mealAnnual,
      bonusAnnual, specialAllow, empPF, gratuity,
      eePF, profTax, esic, totalEeDed,
    });
    setResTab(0);
  }

  return (
    <div className="sc-page">

      {/* ── HERO ── */}
      <div className="sc-hero">
        <span className="sc-hero-badge">💼 FY 2026–27 · In-Hand Calculator</span>
        <h1>
          Salary{" "}
          <span className="choose-title-accent">Calculator</span>
        </h1>
        <p>Decode your CTC → Gross → Take-Home with every component explained</p>
      </div>

      <div className="sc-wrapper">

        {/* ════ FORM PANEL ════ */}
        <div className="sc-panel">

          {/* Tab bar */}
          <div className="sc-tab-bar">
            {FORM_TABS.map((t, i) => (
              <button
                key={t}
                className={`sc-tab${formTab === i ? " active" : ""}`}
                onClick={() => setFormTab(i)}
              >
                <span className="sc-tab-icon">{FORM_ICONS[i]}</span>
                {t}
              </button>
            ))}
          </div>

          <div className="sc-panel-body">

            {/* Tab 0 – Structure */}
            {formTab === 0 && (
              <>
                <Field label="Annual CTC (Cost to Company)">
                  <RupeeInput value={form.ctc} onChange={(v) => upd("ctc", v)} placeholder="e.g. 1300100" />
                </Field>
                <SliderField
                  label="Basic Salary as % of CTC"
                  value={form.basicPct} min={30} max={60}
                  onChange={(v) => upd("basicPct", v)}
                  leftLabel="30%" rightLabel="60%"
                />
                <SliderField
                  label="HRA as % of Basic"
                  value={form.hraPct} min={40} max={60}
                  onChange={(v) => upd("hraPct", v)}
                  leftLabel="40%" rightLabel="60%"
                />
                <Field label="City Type (HRA Exemption)">
                  <Toggle
                    options={[["metro", "🏙 Metro"], ["nonmetro", "🌆 Non-Metro"]]}
                    value={form.cityType}
                    onChange={(v) => upd("cityType", v)}
                  />
                </Field>
              </>
            )}

            {/* Tab 1 – Allowances */}
            {formTab === 1 && (
              <>
                <div className="sc-grid2">
                  <Field label="LTA (Annual)">
                    <RupeeInput value={form.lta} onChange={(v) => upd("lta", v)} placeholder="e.g. 24000" />
                  </Field>
                  <Field label="Meal Allowance (Monthly)">
                    <RupeeInput value={form.mealMonthly} onChange={(v) => upd("mealMonthly", v)} placeholder="e.g. 2200" />
                  </Field>
                </div>
                <Field label="Variable / Performance Bonus (Annual)">
                  <RupeeInput value={form.bonus} onChange={(v) => upd("bonus", v)} placeholder="0" />
                </Field>
                <div className="sc-info-box sc-info-box--green">
                  💡 LTA is exempt 2× in a block of 4 years. Meal allowance up to ₹2,200/month is
                  tax-free under the old regime.
                </div>
              </>
            )}

            {/* Tab 2 – Deductions */}
            {formTab === 2 && (
              <>
                <Field label="Professional Tax (Monthly)">
                  <RupeeInput value={form.profTaxMonthly} onChange={(v) => upd("profTaxMonthly", v)} />
                </Field>
                <Field label="Employer PF Contribution">
                  <Toggle
                    options={[[true, "In CTC"], [false, "Not in CTC"]]}
                    value={form.pfInCTC}
                    onChange={(v) => upd("pfInCTC", v)}
                  />
                </Field>
                <Field label="Gratuity">
                  <Toggle
                    options={[[true, "In CTC"], [false, "Not in CTC"]]}
                    value={form.gratuityInCTC}
                    onChange={(v) => upd("gratuityInCTC", v)}
                  />
                </Field>
                <div className="sc-info-box sc-info-box--blue">
                  <p>🔹 Employee PF = 12% of Basic (max ₹1,800/mo)</p>
                  <p>🔹 Employer PF = 12% of Basic (max ₹1,800/mo)</p>
                  <p>🔹 Gratuity = 4.81% of Basic deducted from CTC</p>
                  <p>🔹 ESIC applies if Gross ≤ ₹21,000/mo @ 0.75%</p>
                </div>
              </>
            )}

          </div>

          {/* Step navigation */}
          <div className="sc-step-nav">
            {formTab > 0 && (
              <button className="sc-btn-ghost" onClick={() => setFormTab(formTab - 1)}>
                ← Back
              </button>
            )}
            {formTab < 2 ? (
              <button className="sc-btn-primary" onClick={() => setFormTab(formTab + 1)}>
                Next: {FORM_TABS[formTab + 1]} →
              </button>
            ) : (
              <button className="sc-btn-primary sc-btn-calc" onClick={calculate}>
                Calculate My Salary →
              </button>
            )}
          </div>

          {/* Progress dots */}
          <div className="sc-dots">
            {FORM_TABS.map((_, i) => (
              <div
                key={i}
                className={`sc-dot${formTab === i ? " active" : ""}`}
                onClick={() => setFormTab(i)}
              />
            ))}
          </div>
        </div>

        {/* ════ RESULTS COLUMN ════ */}
        <div className="sc-result-col">
          {!result ? (
            <div className="sc-empty">
              <div className="sc-empty-icon">💼</div>
              <h3>Enter your CTC to get started</h3>
              <p>Get instant breakdown with take-home pay, PF, HRA exemption and more</p>
              <div className="sc-empty-tags">
                <span>Basic · HRA · LTA</span>
                <span>PF · Gratuity · ESIC</span>
                <span>Monthly Take-Home</span>
              </div>
            </div>
          ) : (
            <div className="sc-results-wrap">

              {/* Hero stats */}
              <div className="sc-stat-row">
                <div className="sc-stat-hero">
                  <div className="sc-stat-lbl">Monthly Take-Home</div>
                  <div className="sc-stat-hero-val">{inr(result.netMonthly)}</div>
                  <div className="sc-stat-note">After all deductions</div>
                </div>
                <div className="sc-stat-mini-grid">
                  <StatMini label="Annual CTC" value={inr(result.ctc)} />
                  <StatMini label="Gross Salary" value={inr(result.grossAnnual)} />
                  <StatMini label="Annual In-Hand" value={inr(result.netAnnual)} colorClass="sc-green" />
                  <StatMini label="Total Deductions" value={inr(result.totalEeDed)} colorClass="sc-red" />
                </div>
              </div>

              {/* Result tab panel */}
              <div className="sc-panel">
                <div className="sc-tab-bar">
                  {RES_TABS.map((t, i) => (
                    <button
                      key={t}
                      className={`sc-tab${resTab === i ? " active" : ""}`}
                      onClick={() => setResTab(i)}
                    >
                      {t}
                    </button>
                  ))}
                </div>

                {/* Earnings tab */}
                {resTab === 0 && (
                  <div>
                    <TableHead />
                    <TableRow label="Basic Salary" annual={result.basic} monthly={result.basic / 12} badge="Taxable" />
                    {result.hraAnnual > 0 && <TableRow label="HRA" annual={result.hraAnnual} monthly={result.hraAnnual / 12} badge="Partly Exempt" />}
                    {result.ltaAnnual > 0 && <TableRow label="LTA" annual={result.ltaAnnual} monthly={result.ltaAnnual / 12} badge="Exempt" />}
                    {result.mealAnnual > 0 && <TableRow label="Meal Allowance" annual={result.mealAnnual} monthly={result.mealAnnual / 12} badge="Exempt" />}
                    {result.specialAllow > 0 && <TableRow label="Special Allowance" annual={result.specialAllow} monthly={result.specialAllow / 12} badge="Taxable" />}
                    {result.bonusAnnual > 0 && <TableRow label="Performance Bonus" annual={result.bonusAnnual} monthly={result.bonusAnnual / 12} badge="Taxable" />}
                    <TableRow label="Gross Salary" annual={result.grossAnnual} monthly={result.grossMonthly} isTotal />
                  </div>
                )}

                {/* Deductions tab */}
                {resTab === 1 && (
                  <div>
                    <TableHead />
                    {result.eePF > 0 && <TableRow label="Employee PF (12% Basic)" annual={result.eePF} monthly={result.eePF / 12} badge="Deduction" />}
                    {result.profTax > 0 && <TableRow label="Professional Tax" annual={result.profTax} monthly={result.profTax / 12} badge="Deduction" />}
                    {result.esic > 0 && <TableRow label="ESIC (0.75%)" annual={result.esic} monthly={result.esic / 12} badge="Deduction" />}
                    <TableRow label="Total Deductions" annual={result.totalEeDed} monthly={result.totalEeDed / 12} isTotal />
                    {(result.empPF > 0 || result.gratuity > 0) && (
                      <>
                        <div className="sc-sub-section-hd">🏢 Employer Contributions (in CTC)</div>
                        {result.empPF > 0 && <TableRow label="Employer PF (12% Basic)" annual={result.empPF} monthly={result.empPF / 12} badge="PF" />}
                        {result.gratuity > 0 && <TableRow label="Gratuity (4.81% Basic)" annual={result.gratuity} monthly={result.gratuity / 12} badge="Gratuity" />}
                      </>
                    )}
                  </div>
                )}

                {/* Distribution tab */}
                {resTab === 2 && (
                  <div className="sc-dist-body">
                    <DistBar label="Take-Home Pay" value={result.netAnnual} total={result.ctc} color="#263012" />
                    {result.eePF > 0 && <DistBar label="Employee PF" value={result.eePF} total={result.ctc} color="#263012" />}
                    {result.empPF > 0 && <DistBar label="Employer PF" value={result.empPF} total={result.ctc} color="#3b82f6" />}
                    {result.gratuity > 0 && <DistBar label="Gratuity" value={result.gratuity} total={result.ctc} color="#a855f7" />}
                    {(result.profTax + result.esic) > 0 && (
                      <DistBar label="Prof. Tax + ESIC" value={result.profTax + result.esic} total={result.ctc} color="#7c3aed" />
                    )}
                    <div className="sc-stack-bar">
                      {[
                        { v: result.netAnnual, c: "#263012" },
                        { v: result.eePF, c: "#263012" },
                        { v: result.empPF, c: "#3b82f6" },
                        { v: result.gratuity, c: "#a855f7" },
                        { v: result.profTax + result.esic, c: "#7c3aed" },
                      ].filter((x) => x.v > 0).map((x, i) => (
                        <div key={i} className="sc-stack-seg" style={{ width: `${(x.v / result.ctc) * 100}%`, background: x.c }} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* HRA note */}
              {result.hraAnnual > 0 && (
                <div className="sc-hra-note">
                  <span>🏠</span>
                  <div>
                    <strong>HRA Tax-Exempt Estimate: {inr(result.hraExempt)}/year</strong> — based on{" "}
                    {form.cityType === "metro" ? "50%" : "40%"} of Basic for{" "}
                    {form.cityType === "metro" ? "Metro" : "Non-Metro"} city.
                    Actual exemption depends on rent paid — consult your CA.
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
}